import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { 
  Search, AlertTriangle, CheckCircle, Clock, Ban, 
  Send, FileText, Bell, Eye, ChevronRight, RefreshCw,
  DollarSign, Users, Calendar, TrendingDown, Wallet, Calculator, Download, QrCode, Copy, Check, Plus, Minus, Receipt,
  ExternalLink, Info
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import PaymentDetailDialog from "./admin/PaymentDetailDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentTracking() {
  const [activeView, setActiveView] = useState("payments");
  const [data, setData] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [sending, setSending] = useState(null);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [playerBalanceData, setPlayerBalanceData] = useState(null);
  
  // Fee Calculator state
  const [showFeeCalculator, setShowFeeCalculator] = useState(false);
  const [showFeeResult, setShowFeeResult] = useState(false);
  const [feeCalcResult, setFeeCalcResult] = useState(null);
  const [calculatingFee, setCalculatingFee] = useState(false);
  const [terms, setTerms] = useState([]);
  const [rateTables, setRateTables] = useState([]);
  const [feeParams, setFeeParams] = useState({
    sessions_per_week: 1,
    num_children: 1,
    child_number: 1,
    is_scc_member: false,
    is_section_member: false,
    is_digital_member: false,
    term_id: "",
    rate_table_id: "",
    term_weeks: 10,
    include_registration_fee: false,
    include_ad_hoc: false,
    ad_hoc_sessions: 0,
    actual_paid: "",
    parent_name: "",
    child_name: ""
  });
  const [copiedRef, setCopiedRef] = useState(false);

  // Payment Detail drill-down state
  const [detailOpenId, setDetailOpenId] = useState(null);
  const feeQrRef = useRef(null);
  const feeExportRef = useRef(null);

  useEffect(() => {
    if (activeView === "payments") {
      fetchPaymentData();
    } else {
      fetchBalanceData();
    }
  }, [statusFilter, activeView]);

  useEffect(() => {
    // Fetch terms and rate tables for fee calculator
    fetchTerms();
    fetchRateTables();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      setTerms(response.data || []);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  const fetchRateTables = async () => {
    try {
      const response = await axios.get(`${API}/academy/rate-tables`, { withCredentials: true });
      setRateTables(response.data || []);
    } catch (error) {
      console.error("Error fetching rate tables:", error);
    }
  };

  const calculateFee = async () => {
    try {
      setCalculatingFee(true);
      const response = await axios.post(`${API}/academy/admin/fee-calculator`, {
        ...feeParams,
        actual_paid: feeParams.actual_paid ? parseFloat(feeParams.actual_paid) : null
      }, { withCredentials: true });
      setFeeCalcResult(response.data);
      setShowFeeResult(true);
    } catch (error) {
      console.error("Error calculating fee:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate fee");
    } finally {
      setCalculatingFee(false);
    }
  };

  const downloadFeeQR = async () => {
    if (!feeQrRef.current) return;
    try {
      const canvas = await html2canvas(feeQrRef.current, {
        backgroundColor: "#1e293b",
        scale: 2
      });
      const link = document.createElement("a");
      link.download = `fee-${feeCalcResult?.qr_data?.reference || "payment"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("QR code downloaded!");
    } catch (error) {
      console.error("Error downloading QR:", error);
      toast.error("Failed to download QR code");
    }
  };

  const downloadFullCalculation = async () => {
    if (!feeExportRef.current) return;
    try {
      const canvas = await html2canvas(feeExportRef.current, {
        backgroundColor: "#0f172a",
        scale: 2,
        useCORS: true
      });
      const link = document.createElement("a");
      link.download = `payment-request-${feeCalcResult?.qr_data?.reference || "calculation"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Full calculation exported!");
    } catch (error) {
      console.error("Error downloading calculation:", error);
      toast.error("Failed to export calculation");
    }
  };

  const copyPaymentRef = () => {
    if (feeCalcResult?.qr_data?.reference) {
      navigator.clipboard.writeText(feeCalcResult.qr_data.reference);
      setCopiedRef(true);
      setTimeout(() => setCopiedRef(false), 2000);
      toast.success("Reference copied!");
    }
  };

  const prefillCalculatorFromEnrollment = (enrollment) => {
    // Extract data from the enrollment record
    const sessionsPerWeek = enrollment.sessions?.enrolled || 1;
    const actualPaid = enrollment.payment?.amount || 0;
    const playerName = enrollment.player?.name || "";
    const parentName = enrollment.parent?.name || "";
    
    // Try to determine membership status from parent data
    const isSccMember = enrollment.parent?.is_scc_member || false;
    const isSectionMember = enrollment.parent?.member_type === "section";
    const isDigitalMember = enrollment.parent?.is_digital_member || false;
    
    // Get term info if available
    const termId = enrollment.term_id || "";
    const termWeeks = enrollment.term_weeks || 10;
    
    // Set the fee params
    setFeeParams({
      sessions_per_week: sessionsPerWeek,
      num_children: 1, // Will need to be adjusted manually if multiple children
      child_number: 1,
      is_scc_member: isSccMember,
      is_section_member: isSectionMember,
      is_digital_member: isDigitalMember,
      term_id: termId,
      rate_table_id: "",
      term_weeks: termWeeks,
      include_registration_fee: enrollment.registration_fee > 0,
      include_ad_hoc: false,
      ad_hoc_sessions: 0,
      actual_paid: actualPaid.toString(),
      parent_name: parentName,
      child_name: playerName
    });
    
    setShowFeeCalculator(true);
    toast.success(`Calculator pre-filled for ${playerName}`);
  };

  const fetchPaymentData = async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const response = await axios.get(`${API}/academy/admin/payment-tracking${params}`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching payment data:", error);
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBalanceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/admin/session-balance-overview`, { withCredentials: true });
      setBalanceData(response.data);
    } catch (error) {
      console.error("Error fetching balance data:", error);
      toast.error("Failed to load session balance data");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerBalance = async (playerId) => {
    try {
      const response = await axios.get(`${API}/academy/player/${playerId}/session-balance`, { withCredentials: true });
      setPlayerBalanceData(response.data);
      setShowBalanceModal(true);
    } catch (error) {
      console.error("Error fetching player balance:", error);
      toast.error("Failed to load player balance");
    }
  };

  const fetchAuditTrail = async (playerId) => {
    try {
      const response = await axios.get(`${API}/academy/admin/audit-trail/${playerId}`, { withCredentials: true });
      setAuditData(response.data);
      setShowAuditModal(true);
    } catch (error) {
      console.error("Error fetching audit trail:", error);
      toast.error("Failed to load audit trail");
    }
  };

  const sendInvoice = async (enrollmentId) => {
    try {
      setSending(`invoice-${enrollmentId}`);
      await axios.post(`${API}/academy/admin/send-invoice/${enrollmentId}`, {}, { withCredentials: true });
      toast.success("Invoice sent successfully");
      fetchPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSending(null);
    }
  };

  const sendReminder = async (enrollmentId) => {
    try {
      setSending(`reminder-${enrollmentId}`);
      await axios.post(`${API}/academy/admin/send-reminder/${enrollmentId}`, {}, { withCredentials: true });
      toast.success("Reminder sent successfully");
      fetchPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminder");
    } finally {
      setSending(null);
    }
  };

  const sendSuspensionNotice = async (enrollmentId) => {
    try {
      setSending(`suspend-${enrollmentId}`);
      await axios.post(`${API}/academy/admin/send-suspension-notice/${enrollmentId}`, {}, { withCredentials: true });
      toast.success("Suspension notice sent");
      fetchPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send suspension notice");
    } finally {
      setSending(null);
    }
  };

  const markAsPaid = async (enrollmentId) => {
    try {
      setSending(`paid-${enrollmentId}`);
      await axios.put(`${API}/academy/admin/payment-status/${enrollmentId}`, {
        enrollment_id: enrollmentId,
        payment_status: "approved",
        payment_method: "manual"
      }, { withCredentials: true });
      toast.success("Marked as paid");
      fetchPaymentData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setSending(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <span className="px-2 py-1 rounded-full text-xs bg-emerald-500/20 text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Paid</span>;
      case "pending":
        return <span className="px-2 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case "overdue":
        return <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Overdue</span>;
      case "waived":
        return <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Waived</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs bg-slate-500/20 text-slate-400">{status}</span>;
    }
  };

  const filteredEnrollments = data?.enrollments?.filter(e => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      e.player?.name?.toLowerCase().includes(search) ||
      e.parent?.name?.toLowerCase().includes(search) ||
      e.invoice_reference?.toLowerCase().includes(search)
    );
  }) || [];

  const filteredBalances = balanceData?.players?.filter(p => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return p.player_name?.toLowerCase().includes(search);
  }) || [];

  if (loading && !data && !balanceData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-tracking">
      {/* Feb 2026 audit fix — amber banner steers admins to the unified
          Payments Hub for any actions. This view is kept read-only for
          historical visibility (per-enrollment + manual-invoice rows that
          predate the Hub). The Mark-as-Paid button on each row is also
          replaced with an "Open in Payments Hub" link to prevent the
          desync class where AdminPanel writes academy_enrollments but
          payment_transactions stays awaiting_payment. */}
      <div
        className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3"
        data-testid="payment-tracking-readonly-banner"
      >
        <Info className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-100">
          <p className="font-semibold mb-1">This view is read-only.</p>
          <p className="text-amber-100/90">
            For all payment actions including approving, sending invoices, and sending reminders,
            please use the{" "}
            <Link
              to="/admin/payments"
              className="font-semibold text-white underline hover:text-amber-200"
              data-testid="payment-tracking-banner-hub-link"
            >
              Payments Hub at /admin/payments
            </Link>{" "}
            which is the single source of truth.
          </p>
        </div>
      </div>

      {/* View Tabs */}
      <Tabs value={activeView} onValueChange={setActiveView} className="w-full">
        <TabsList className="bg-slate-800/50 border border-white/5">
          <TabsTrigger value="payments" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <DollarSign className="w-4 h-4 mr-2" /> Payment Tracking
          </TabsTrigger>
          <TabsTrigger value="balance" className="data-[state=active]:bg-yellow-500 data-[state=active]:text-black">
            <Wallet className="w-4 h-4 mr-2" /> Session Balance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="payments" className="mt-6">
          {/* Payment Tracking Content */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 bg-gradient-to-br from-slate-700/50 to-slate-800/30 border border-white/5">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-slate-400" />
            <div>
              <p className="text-2xl font-bold text-white">{data?.summary?.total_enrollments || 0}</p>
              <p className="text-xs text-slate-400">Total Enrollments</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 cursor-pointer hover:border-amber-400" onClick={() => setStatusFilter("pending")}>
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-400" />
            <div>
              <p className="text-2xl font-bold text-white">{data?.summary?.pending_count || 0}</p>
              <p className="text-xs text-slate-400">Pending</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30 cursor-pointer hover:border-red-400" onClick={() => setStatusFilter("overdue")}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <div>
              <p className="text-2xl font-bold text-white">{data?.summary?.overdue_count || 0}</p>
              <p className="text-xs text-slate-400">Overdue</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 cursor-pointer hover:border-emerald-400" onClick={() => setStatusFilter("approved")}>
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
            <div>
              <p className="text-2xl font-bold text-white">{data?.summary?.approved_count || 0}</p>
              <p className="text-xs text-slate-400">Paid</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-600/30 to-red-700/20 border border-red-600/50">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-red-400" />
            <div>
              <p className="text-2xl font-bold text-white">${data?.summary?.total_amount_pending?.toFixed(2) || "0.00"}</p>
              <p className="text-xs text-slate-400">Outstanding</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by child, parent, or invoice..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 bg-slate-800/50 border-slate-700 text-white"
            data-testid="payment-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="approved">Paid</SelectItem>
            <SelectItem value="waived">Waived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchPaymentData} className="border-slate-700">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            window.open(`${API}/academy/admin/payment-export`, "_blank");
          }}
          className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
          data-testid="export-payments-btn"
          title="Export all payments with fee breakdown to CSV"
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
        <Button 
          onClick={() => setShowFeeCalculator(true)} 
          className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
          data-testid="fee-calculator-btn"
        >
          <Calculator className="w-4 h-4 mr-2" /> Fee Calculator
        </Button>
      </div>

      {/* Enrollments Table */}
      <Card className="bg-slate-900/50 border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Child</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Parent</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Sessions</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Unpaid Attended</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {filteredEnrollments.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-400">
                    {searchTerm ? "No matching enrollments found" : "No enrollments to display"}
                  </td>
                </tr>
              ) : (
                filteredEnrollments.map((enrollment) => (
                  <tr key={`${enrollment.source || 'row'}-${enrollment.enrollment_id}-${enrollment.player?.id || ''}`} className="hover:bg-slate-800/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white font-medium">
                          {enrollment.player?.name || "Unknown"}
                          {enrollment.source === "manual_invoice" && (
                            <span className="ml-2 inline-block text-[10px] bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded border border-amber-500/40 align-middle" title="Ad-hoc manual invoice (not tied to a term enrollment)">
                              MANUAL
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400">
                          {enrollment.source === "manual_invoice"
                            ? (enrollment.payment?.description || "Manual invoice")
                            : (enrollment.player?.age_group || "-")}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-white">{enrollment.parent?.name || "Unknown"}</p>
                        <p className="text-xs text-slate-400">{enrollment.parent?.email || "-"}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-center">
                        <p className="text-white">{enrollment.sessions?.enrolled || 0}</p>
                        {enrollment.sessions?.session_days?.length > 0 && (
                          <p className="text-xs text-blue-400 mt-1">
                            {enrollment.sessions.session_days.join(", ")}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {enrollment.sessions?.attended_unpaid > 0 ? (
                        <span className="text-red-400 font-bold">{enrollment.sessions?.attended_unpaid}</span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>
                        <p className="text-white font-medium">${enrollment.payment?.amount?.toFixed(2) || "0.00"}</p>
                        {(enrollment.registration_fee > 0 || enrollment.term_fee > 0) && (
                          <p className="text-xs text-slate-400">
                            {enrollment.registration_fee > 0 && `Reg: $${enrollment.registration_fee}`}
                            {enrollment.registration_fee > 0 && enrollment.term_fee > 0 && " + "}
                            {enrollment.term_fee > 0 && `Term: $${enrollment.term_fee}`}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1">
                        {getStatusBadge(enrollment.payment?.status)}
                        {enrollment.payment?.status_source && (
                          <span
                            className={`text-[10px] leading-none px-1.5 py-0.5 rounded-full border ${
                              enrollment.payment?.status_drift
                                ? "bg-amber-500/10 text-amber-300 border-amber-500/40"
                                : enrollment.payment.status_source === "transaction"
                                ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"
                                : enrollment.payment.status_source === "enrollment"
                                ? "bg-sky-500/10 text-sky-300 border-sky-500/30"
                                : "bg-slate-500/10 text-slate-400 border-slate-500/30"
                            }`}
                            title={
                              enrollment.payment?.status_drift
                                ? "Status drift: registration record is out of sync with the payment transaction. Run M4 backfill to heal."
                                : `Status source: ${enrollment.payment.status_source}`
                            }
                            data-testid={`status-source-${enrollment.enrollment_id}`}
                          >
                            {enrollment.payment?.status_drift
                              ? "drift"
                              : enrollment.payment.status_source === "transaction"
                              ? "txn"
                              : enrollment.payment.status_source === "enrollment"
                              ? "enroll"
                              : "reg"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {enrollment.payment?.status !== "approved" && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-slate-400 hover:text-white"
                              onClick={() => sendInvoice(enrollment.enrollment_id)}
                              disabled={sending === `invoice-${enrollment.enrollment_id}`}
                              title="Send Invoice"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-amber-400 hover:text-amber-300"
                              onClick={() => sendReminder(enrollment.enrollment_id)}
                              disabled={sending === `reminder-${enrollment.enrollment_id}`}
                              title="Send Reminder"
                            >
                              <Bell className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-2 text-red-400 hover:text-red-300"
                              onClick={() => sendSuspensionNotice(enrollment.enrollment_id)}
                              disabled={sending === `suspend-${enrollment.enrollment_id}`}
                              title="Send Suspension Notice"
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                            {/* Feb 2026 audit fix — Mark-as-Paid button
                                replaced with a deep-link to the Payments Hub
                                pre-filtered to this parent. The legacy
                                PUT /api/academy/admin/payment-status path
                                used to write academy_enrollments directly,
                                bypassing process_successful_payment and
                                leaving payment_transactions in awaiting_payment.
                                The Hub is now the single source of truth. */}
                            <Link
                              to={`/admin/payments?q=${encodeURIComponent(enrollment.parent_email || enrollment.parent_name || "")}`}
                              data-testid={`open-in-hub-${enrollment.enrollment_id}`}
                              title="Open this parent in the Payments Hub to mark paid (read-only here)"
                            >
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 px-2 text-emerald-400 hover:text-emerald-300"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </Link>
                          </>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-purple-400 hover:text-purple-300"
                          onClick={() => prefillCalculatorFromEnrollment(enrollment)}
                          title="Calculate Fee"
                          data-testid={`calc-fee-${enrollment.enrollment_id}`}
                        >
                          <Calculator className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-emerald-400 hover:text-emerald-300"
                          onClick={() => setDetailOpenId(enrollment.enrollment_id)}
                          title="View Payment Detail (what was paid for?)"
                          data-testid={`payment-detail-${enrollment.enrollment_id}`}
                        >
                          <Receipt className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-blue-400 hover:text-blue-300 disabled:opacity-40"
                          onClick={() => {
                            const pid = enrollment.player?.id;
                            if (!pid) {
                              toast.error("Audit trail unavailable — this registration has no player record yet (complete enrollment approval first).");
                              return;
                            }
                            fetchAuditTrail(pid);
                          }}
                          disabled={!enrollment.player?.id}
                          title={enrollment.player?.id ? "View Audit Trail" : "Audit trail unavailable — no player record yet"}
                          data-testid={`audit-trail-${enrollment.enrollment_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Audit Trail Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-400" />
              Audit Trail: {auditData?.player?.name || "Player"}
            </DialogTitle>
          </DialogHeader>
          
          {auditData && (
            <div className="space-y-6">
              {/* Player & Parent Info */}
              <div className="grid grid-cols-2 gap-4">
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Child</h4>
                  <p className="text-white font-medium">{auditData.player?.name}</p>
                  <p className="text-sm text-slate-400">{auditData.player?.age_group} | #{auditData.player?.membership_number}</p>
                </Card>
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-2">Parent</h4>
                  <p className="text-white font-medium">{auditData.parent?.name || "Not linked"}</p>
                  <p className="text-sm text-slate-400">{auditData.parent?.email}</p>
                  <p className="text-sm text-slate-400">{auditData.parent?.phone}</p>
                </Card>
              </div>

              {/* Financial Summary */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Financial Summary</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">${auditData.financial?.total_paid?.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Total Paid</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">${auditData.financial?.total_unpaid?.toFixed(2)}</p>
                    <p className="text-xs text-slate-400">Total Unpaid</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{auditData.financial?.enrollments_paid}</p>
                    <p className="text-xs text-slate-400">Enrollments Paid</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-400">{auditData.financial?.enrollments_unpaid}</p>
                    <p className="text-xs text-slate-400">Enrollments Unpaid</p>
                  </div>
                </div>
              </Card>

              {/* Attendance Summary */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <h4 className="text-sm font-medium text-slate-400 mb-3">Attendance Summary</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">{auditData.attendance?.summary?.total_sessions}</p>
                    <p className="text-xs text-slate-400">Total Sessions</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">{auditData.attendance?.summary?.total_present}</p>
                    <p className="text-xs text-slate-400">Present</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-400">{auditData.attendance?.summary?.total_absent}</p>
                    <p className="text-xs text-slate-400">Absent</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-400">{auditData.attendance?.summary?.total_mc}</p>
                    <p className="text-xs text-slate-400">MC</p>
                  </div>
                </div>
              </Card>

              {/* Attendance Records */}
              {auditData.attendance?.records?.length > 0 && (
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Attendance Records (Proof of Attendance)</h4>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {auditData.attendance.records.map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-700/50 rounded">
                        <div>
                          <p className="text-white text-sm">
                            {record.session?.session_date ? new Date(record.session.session_date).toLocaleDateString() : "Unknown date"}
                          </p>
                          <p className="text-xs text-slate-400">{record.session?.location}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          record.status === "present" ? "bg-emerald-500/20 text-emerald-400" :
                          record.status === "absent" ? "bg-red-500/20 text-red-400" :
                          "bg-blue-500/20 text-blue-400"
                        }`}>
                          {record.status?.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

        </TabsContent>

        {/* Session Balance Tab */}
        <TabsContent value="balance" className="mt-6">
          {/* Balance Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <Card className="p-4 bg-gradient-to-br from-slate-700/50 to-slate-800/30 border border-white/5">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-slate-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{balanceData?.summary?.total_players || 0}</p>
                  <p className="text-xs text-slate-400">Total Players</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30">
              <div className="flex items-center gap-3">
                <TrendingDown className="w-8 h-8 text-amber-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{balanceData?.summary?.players_low_balance || 0}</p>
                  <p className="text-xs text-slate-400">Low Balance (≤2)</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/30">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{balanceData?.summary?.players_zero_balance || 0}</p>
                  <p className="text-xs text-slate-400">Zero Balance</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{balanceData?.summary?.players_unpaid || 0}</p>
                  <p className="text-xs text-slate-400">Unpaid</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
              <div className="flex items-center gap-3">
                <Wallet className="w-8 h-8 text-emerald-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{balanceData?.summary?.total_sessions_remaining || 0}</p>
                  <p className="text-xs text-slate-400">Total Remaining</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Search */}
          <div className="flex gap-4 items-center mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by player name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            <Button variant="outline" onClick={fetchBalanceData} className="border-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>

          {/* Balance Table */}
          <Card className="bg-slate-900/50 border border-white/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Player</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Age Group</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Purchased</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Used</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Remaining</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Payment</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {filteredBalances.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-slate-400">
                        No players with enrollments found
                      </td>
                    </tr>
                  ) : (
                    filteredBalances.map((player) => (
                      <tr key={player.player_id} className="hover:bg-slate-800/30">
                        <td className="px-4 py-3">
                          <p className="text-white font-medium">{player.player_name}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-300">{player.age_group || "-"}</td>
                        <td className="px-4 py-3 text-center text-white">{player.total_purchased}</td>
                        <td className="px-4 py-3 text-center text-white">{player.total_used}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-bold ${
                            player.total_remaining === 0 ? "text-red-400" :
                            player.total_remaining <= 2 ? "text-amber-400" :
                            "text-emerald-400"
                          }`}>
                            {player.total_remaining}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(player.payment_status)}</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-blue-400 hover:text-blue-300"
                            onClick={() => fetchPlayerBalance(player.player_id)}
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Player Balance Detail Modal */}
      <Dialog open={showBalanceModal} onOpenChange={setShowBalanceModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Wallet className="w-5 h-5 text-emerald-400" />
              Session Balance: {playerBalanceData?.player?.name || "Player"}
            </DialogTitle>
          </DialogHeader>
          
          {playerBalanceData && (
            <div className="space-y-6">
              {/* Balance Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 bg-slate-800/50 border-slate-700 text-center">
                  <p className="text-3xl font-bold text-white">{playerBalanceData.balance?.total_purchased}</p>
                  <p className="text-xs text-slate-400">Purchased</p>
                </Card>
                <Card className="p-4 bg-slate-800/50 border-slate-700 text-center">
                  <p className="text-3xl font-bold text-amber-400">{playerBalanceData.balance?.total_used}</p>
                  <p className="text-xs text-slate-400">Used</p>
                </Card>
                <Card className="p-4 bg-slate-800/50 border-slate-700 text-center">
                  <p className={`text-3xl font-bold ${
                    playerBalanceData.balance?.total_remaining === 0 ? "text-red-400" :
                    playerBalanceData.balance?.total_remaining <= 2 ? "text-amber-400" :
                    "text-emerald-400"
                  }`}>{playerBalanceData.balance?.total_remaining}</p>
                  <p className="text-xs text-slate-400">Remaining</p>
                </Card>
              </div>

              {/* Enrollments */}
              {playerBalanceData.enrollments?.length > 0 && (
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Enrollment History</h4>
                  <div className="space-y-2">
                    {playerBalanceData.enrollments.map((enrollment, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-700/50 rounded">
                        <div>
                          <p className="text-white text-sm font-medium">{enrollment.term_name}</p>
                          <p className="text-xs text-slate-400">
                            {enrollment.sessions_per_week}x/week | Days: {enrollment.enrolled_days?.map(d => 
                              ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d]
                            ).join(", ") || "-"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-white text-sm">{enrollment.sessions_remaining} / {enrollment.sessions_purchased}</p>
                          <p className="text-xs text-slate-400">remaining</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Upcoming Sessions */}
              {playerBalanceData.upcoming_sessions?.length > 0 && (
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-400 mb-3">Upcoming Sessions</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {playerBalanceData.upcoming_sessions.map((session, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 px-3 bg-slate-700/50 rounded">
                        <div>
                          <p className="text-white text-sm">
                            {new Date(session.session_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </p>
                          <p className="text-xs text-slate-400">{session.start_time} @ {session.location}</p>
                        </div>
                        {session.attendance_taken ? (
                          <span className="px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-400">Attended</span>
                        ) : (
                          <span className="px-2 py-1 rounded text-xs bg-slate-500/20 text-slate-400">Scheduled</span>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fee Calculator Modal */}
      <Dialog open={showFeeCalculator} onOpenChange={setShowFeeCalculator}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-400" />
              Fee Calculator
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Names Section */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Parent Name (optional)</Label>
                <Input
                  value={feeParams.parent_name}
                  onChange={(e) => setFeeParams(prev => ({ ...prev, parent_name: e.target.value }))}
                  placeholder="Parent's name"
                  className="bg-slate-800/50 border-slate-700 text-white"
                  data-testid="fee-parent-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Child Name (optional)</Label>
                <Input
                  value={feeParams.child_name}
                  onChange={(e) => setFeeParams(prev => ({ ...prev, child_name: e.target.value }))}
                  placeholder="Child's name"
                  className="bg-slate-800/50 border-slate-700 text-white"
                  data-testid="fee-child-name"
                />
              </div>
            </div>

            {/* Term & Rate Table Selection */}
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Term / Rate Table
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Select Term</Label>
                  <Select 
                    value={feeParams.term_id} 
                    onValueChange={(value) => {
                      const selectedTerm = terms.find(t => t.id === value);
                      setFeeParams(prev => ({ 
                        ...prev, 
                        term_id: value,
                        term_weeks: selectedTerm?.total_weeks || prev.term_weeks,
                        rate_table_id: selectedTerm?.rate_table_id || prev.rate_table_id
                      }));
                    }}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="fee-term-select">
                      <SelectValue placeholder="Select term..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom (enter weeks)</SelectItem>
                      {terms.map(term => (
                        <SelectItem key={term.id} value={term.id}>{term.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Term Weeks</Label>
                  <Input
                    type="number"
                    min={1}
                    max={52}
                    value={feeParams.term_weeks}
                    onChange={(e) => setFeeParams(prev => ({ ...prev, term_weeks: parseInt(e.target.value) || 10 }))}
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="fee-term-weeks"
                  />
                </div>
              </div>
            </Card>

            {/* Sessions Configuration */}
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Sessions & Children
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Sessions per Week <span className="text-purple-400">(per child)</span></Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-slate-600"
                      onClick={() => setFeeParams(prev => ({ ...prev, sessions_per_week: Math.max(1, prev.sessions_per_week - 1) }))}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="text-white font-bold text-lg w-8 text-center" data-testid="fee-sessions-value">{feeParams.sessions_per_week}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-slate-600"
                      onClick={() => setFeeParams(prev => ({ ...prev, sessions_per_week: Math.min(7, prev.sessions_per_week + 1) }))}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">Children in Family</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-slate-600"
                      onClick={() => setFeeParams(prev => ({ 
                        ...prev, 
                        num_children: Math.max(1, prev.num_children - 1),
                        child_number: Math.min(prev.child_number, Math.max(1, prev.num_children - 1))
                      }))}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="text-white font-bold text-lg w-8 text-center">{feeParams.num_children}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 border-slate-600"
                      onClick={() => setFeeParams(prev => ({ ...prev, num_children: Math.min(5, prev.num_children + 1) }))}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-400 text-xs">This is Child #</Label>
                  <Select 
                    value={feeParams.child_number.toString()} 
                    onValueChange={(val) => setFeeParams(prev => ({ ...prev, child_number: parseInt(val) }))}
                  >
                    <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="fee-child-number">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: feeParams.num_children }, (_, i) => i + 1).map(num => (
                        <SelectItem key={num} value={num.toString()}>
                          {num === 1 ? "1st (no sibling discount)" : `${num}${num === 2 ? 'nd' : num === 3 ? 'rd' : 'th'} child`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Summary calculation */}
              <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <p className="text-sm text-slate-300">
                  <span className="text-purple-400 font-medium">Calculating for:</span>{" "}
                  Child #{feeParams.child_number} of {feeParams.num_children} • {feeParams.sessions_per_week} session{feeParams.sessions_per_week > 1 ? "s" : ""}/week × {feeParams.term_weeks} weeks
                </p>
                {feeParams.num_children > 1 && feeParams.child_number > 1 && (
                  <p className="text-xs text-emerald-400 mt-1">
                    ✓ Sibling discount will be applied (child #{feeParams.child_number})
                  </p>
                )}
              </div>
            </Card>

            {/* Membership Status */}
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Membership Status
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_scc_member"
                    checked={feeParams.is_scc_member}
                    onCheckedChange={(checked) => setFeeParams(prev => ({ ...prev, is_scc_member: checked }))}
                    data-testid="fee-scc-member"
                  />
                  <Label htmlFor="is_scc_member" className="text-slate-300 text-sm cursor-pointer">
                    SCC Club Member
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_section_member"
                    checked={feeParams.is_section_member}
                    onCheckedChange={(checked) => setFeeParams(prev => ({ ...prev, is_section_member: checked }))}
                    data-testid="fee-section-member"
                  />
                  <Label htmlFor="is_section_member" className="text-slate-300 text-sm cursor-pointer">
                    Section Member
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_digital_member"
                    checked={feeParams.is_digital_member}
                    onCheckedChange={(checked) => setFeeParams(prev => ({ ...prev, is_digital_member: checked }))}
                    data-testid="fee-digital-member"
                  />
                  <Label htmlFor="is_digital_member" className="text-slate-300 text-sm cursor-pointer">
                    Digital Member
                  </Label>
                </div>
              </div>
            </Card>

            {/* Additional Fees */}
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Additional Fees
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include_registration_fee"
                    checked={feeParams.include_registration_fee}
                    onCheckedChange={(checked) => setFeeParams(prev => ({ ...prev, include_registration_fee: checked }))}
                    data-testid="fee-include-reg"
                  />
                  <Label htmlFor="include_registration_fee" className="text-slate-300 text-sm cursor-pointer">
                    Include Registration Fee
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="include_ad_hoc"
                    checked={feeParams.include_ad_hoc}
                    onCheckedChange={(checked) => setFeeParams(prev => ({ ...prev, include_ad_hoc: checked, ad_hoc_sessions: checked ? 1 : 0 }))}
                    data-testid="fee-include-adhoc"
                  />
                  <Label htmlFor="include_ad_hoc" className="text-slate-300 text-sm cursor-pointer">
                    Ad-hoc Sessions
                  </Label>
                  {feeParams.include_ad_hoc && (
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={feeParams.ad_hoc_sessions}
                      onChange={(e) => setFeeParams(prev => ({ ...prev, ad_hoc_sessions: parseInt(e.target.value) || 0 }))}
                      className="w-16 h-8 bg-slate-700/50 border-slate-600 text-white ml-2"
                      data-testid="fee-adhoc-count"
                    />
                  )}
                </div>
              </div>
            </Card>

            {/* Compare with Actual Payment */}
            <Card className="p-4 bg-slate-800/50 border-slate-700">
              <h4 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" /> Compare with Actual Payment
              </h4>
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Amount Already Paid ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={feeParams.actual_paid}
                  onChange={(e) => setFeeParams(prev => ({ ...prev, actual_paid: e.target.value }))}
                  placeholder="Leave empty to skip comparison"
                  className="bg-slate-700/50 border-slate-600 text-white"
                  data-testid="fee-actual-paid"
                />
                <p className="text-xs text-slate-500">Enter the amount the parent has actually paid to compare with the calculated fee</p>
              </div>
            </Card>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowFeeCalculator(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={calculateFee} 
              disabled={calculatingFee}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
              data-testid="calculate-fee-btn"
            >
              {calculatingFee ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Calculating...
                </>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" /> Calculate Fee
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fee Calculation Result Modal */}
      <Dialog open={showFeeResult} onOpenChange={setShowFeeResult}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Fee Calculation Result
            </DialogTitle>
          </DialogHeader>
          
          {feeCalcResult && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
                  <p className="text-xs text-slate-400 mb-1">Calculated Total</p>
                  <p className="text-3xl font-bold text-emerald-400">${feeCalcResult.calculation?.calculated_total?.toFixed(2)}</p>
                </Card>
                {feeCalcResult.comparison?.actual_paid !== null && (
                  <>
                    <Card className="p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30">
                      <p className="text-xs text-slate-400 mb-1">Amount Paid</p>
                      <p className="text-3xl font-bold text-blue-400">${feeCalcResult.comparison?.actual_paid?.toFixed(2)}</p>
                    </Card>
                    <Card className={`p-4 bg-gradient-to-br border ${
                      feeCalcResult.comparison?.status === "exact_match" 
                        ? "from-emerald-500/20 to-emerald-600/10 border-emerald-500/30"
                        : feeCalcResult.comparison?.status === "overpaid"
                        ? "from-amber-500/20 to-amber-600/10 border-amber-500/30"
                        : "from-red-500/20 to-red-600/10 border-red-500/30"
                    }`}>
                      <p className="text-xs text-slate-400 mb-1">Difference</p>
                      <p className={`text-3xl font-bold ${
                        feeCalcResult.comparison?.status === "exact_match" 
                          ? "text-emerald-400"
                          : feeCalcResult.comparison?.status === "overpaid"
                          ? "text-amber-400"
                          : "text-red-400"
                      }`}>
                        {feeCalcResult.comparison?.status === "exact_match" ? (
                          <span className="flex items-center gap-2"><CheckCircle className="w-8 h-8" /> Match!</span>
                        ) : (
                          <>
                            {feeCalcResult.comparison?.difference > 0 ? "+" : ""}
                            ${feeCalcResult.comparison?.difference?.toFixed(2)}
                          </>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {feeCalcResult.comparison?.status === "overpaid" && "Overpaid - credit due"}
                        {feeCalcResult.comparison?.status === "underpaid" && "Underpaid - payment needed"}
                      </p>
                    </Card>
                  </>
                )}
              </div>

              {/* Calculation Breakdown */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <h4 className="text-sm font-medium text-slate-300 mb-3">Calculation Parameters</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Sessions/Week (per child)</p>
                    <p className="text-white font-medium">{feeCalcResult.calculation?.parameters?.sessions_per_week} session{feeCalcResult.calculation?.parameters?.sessions_per_week > 1 ? "s" : ""}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Term Weeks</p>
                    <p className="text-white font-medium">{feeCalcResult.calculation?.parameters?.term_weeks} weeks</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Child #</p>
                    <p className="text-white font-medium">
                      {feeCalcResult.calculation?.parameters?.child_number} of {feeCalcResult.calculation?.parameters?.num_children}
                      {feeCalcResult.calculation?.parameters?.child_number > 1 && (
                        <span className="text-emerald-400 text-xs ml-1">(sibling)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Membership</p>
                    <p className="text-white font-medium">
                      {feeCalcResult.calculation?.parameters?.is_section_member ? "Section" :
                       feeCalcResult.calculation?.parameters?.is_scc_member ? "Club" :
                       feeCalcResult.calculation?.parameters?.is_digital_member ? "Digital" : "Non-member"}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Fee Breakdown */}
              <Card className="p-4 bg-slate-800/50 border-slate-700">
                <h4 className="text-sm font-medium text-slate-300 mb-3">
                  Fee Breakdown for Child #{feeCalcResult.calculation?.parameters?.child_number}
                  <span className="text-slate-500 font-normal ml-2">
                    ({feeCalcResult.calculation?.parameters?.sessions_per_week} session{feeCalcResult.calculation?.parameters?.sessions_per_week > 1 ? "s" : ""}/week × {feeCalcResult.calculation?.parameters?.term_weeks} weeks)
                  </span>
                </h4>
                <div className="space-y-2">
                  {feeCalcResult.calculation?.breakdown?.session_breakdown?.map((session, idx) => (
                    <div key={idx} className="flex justify-between py-2 px-3 bg-slate-700/50 rounded text-sm">
                      <span className="text-slate-300">
                        Session {session.session_number}/week × {feeCalcResult.calculation?.parameters?.term_weeks} weeks
                      </span>
                      <div className="text-right">
                        <span className="text-white">${session.final_price?.toFixed(2)}</span>
                        {session.membership_discount > 0 && (
                          <span className="text-xs text-emerald-400 ml-2">(-${session.membership_discount?.toFixed(2)} membership)</span>
                        )}
                        {session.session_count_discount > 0 && (
                          <span className="text-xs text-blue-400 ml-2">(-${session.session_count_discount?.toFixed(2)} multi-session)</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {feeCalcResult.calculation?.breakdown?.sibling_discount > 0 && (
                    <div className="flex justify-between py-2 px-3 bg-emerald-500/10 rounded text-sm">
                      <span className="text-emerald-400">Sibling Discount (Child #{feeCalcResult.calculation?.parameters?.child_number})</span>
                      <span className="text-emerald-400">-${feeCalcResult.calculation?.breakdown?.sibling_discount?.toFixed(2)}</span>
                    </div>
                  )}
                  {feeCalcResult.calculation?.registration_fee > 0 && (
                    <div className="flex justify-between py-2 px-3 bg-slate-700/50 rounded text-sm">
                      <span className="text-slate-300">Registration Fee</span>
                      <span className="text-white">+${feeCalcResult.calculation?.registration_fee?.toFixed(2)}</span>
                    </div>
                  )}
                  {feeCalcResult.calculation?.ad_hoc_fee > 0 && (
                    <div className="flex justify-between py-2 px-3 bg-amber-500/10 rounded text-sm">
                      <span className="text-amber-400">Ad-hoc Sessions ({feeCalcResult.calculation?.parameters?.ad_hoc_sessions}x)</span>
                      <span className="text-amber-400">+${feeCalcResult.calculation?.ad_hoc_fee?.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 px-3 bg-purple-500/20 rounded text-base font-bold border border-purple-500/30">
                    <span className="text-white">Total for Child #{feeCalcResult.calculation?.parameters?.child_number}</span>
                    <span className="text-purple-400">${feeCalcResult.calculation?.calculated_total?.toFixed(2)}</span>
                  </div>
                </div>
              </Card>

              {/* Payment QR Code - Show if underpaid or for full payment */}
              {(feeCalcResult.comparison?.status === "underpaid" || feeCalcResult.comparison?.status === "not_compared") && (
                <Card className="p-4 bg-slate-800/50 border-slate-700" ref={feeQrRef}>
                  <h4 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
                    <QrCode className="w-4 h-4" /> Payment QR Code
                    {feeCalcResult.comparison?.status === "underpaid" && (
                      <span className="text-xs text-red-400 ml-2">(Outstanding Amount)</span>
                    )}
                  </h4>
                  <div className="flex gap-6 items-start">
                    <div className="bg-white p-4 rounded-lg">
                      <QRCodeSVG 
                        value={`https://www.paynow.sg/pay?UEN=${feeCalcResult.qr_data?.uen}&amount=${
                          feeCalcResult.comparison?.status === "underpaid" 
                            ? Math.abs(feeCalcResult.comparison?.difference || 0).toFixed(2)
                            : feeCalcResult.qr_data?.amount?.toFixed(2)
                        }&ref=${feeCalcResult.qr_data?.reference}`}
                        size={150}
                        level="H"
                      />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <p className="text-xs text-slate-500">Pay To</p>
                        <p className="text-white font-medium">{feeCalcResult.qr_data?.company}</p>
                        <p className="text-slate-400 text-sm">UEN: {feeCalcResult.qr_data?.uen}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Amount</p>
                        <p className="text-2xl font-bold text-emerald-400">
                          ${feeCalcResult.comparison?.status === "underpaid" 
                            ? Math.abs(feeCalcResult.comparison?.difference || 0).toFixed(2)
                            : feeCalcResult.qr_data?.amount?.toFixed(2)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Reference</p>
                        <div className="flex items-center gap-2">
                          <code className="text-yellow-400 bg-slate-700/50 px-2 py-1 rounded">{feeCalcResult.qr_data?.reference}</code>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 px-2"
                            onClick={copyPaymentRef}
                          >
                            {copiedRef ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      {(feeParams.parent_name || feeParams.child_name) && (
                        <div>
                          <p className="text-xs text-slate-500">For</p>
                          <p className="text-white">
                            {feeParams.child_name && <span>{feeParams.child_name}</span>}
                            {feeParams.parent_name && feeParams.child_name && <span className="text-slate-400"> (Parent: {feeParams.parent_name})</span>}
                            {feeParams.parent_name && !feeParams.child_name && <span>{feeParams.parent_name}</span>}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Rate Table Info */}
              {feeCalcResult.rate_table_info && (
                <Card className="p-4 bg-slate-800/50 border-slate-700">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Rate Table Configuration</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Pricing Mode</p>
                      <p className="text-white capitalize">{feeCalcResult.rate_table_info?.pricing_mode}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Base Session Price</p>
                      <p className="text-white">${feeCalcResult.rate_table_info?.base_session_price}/week</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Membership Discounts</p>
                      <div className="text-xs space-y-1">
                        {feeCalcResult.rate_table_info?.membership_discounts?.section_member?.value > 0 && (
                          <p className="text-emerald-400">Section: {feeCalcResult.rate_table_info?.membership_discounts?.section_member?.value}%</p>
                        )}
                        {feeCalcResult.rate_table_info?.membership_discounts?.club_member?.value > 0 && (
                          <p className="text-emerald-400">Club: {feeCalcResult.rate_table_info?.membership_discounts?.club_member?.value}%</p>
                        )}
                        {feeCalcResult.rate_table_info?.membership_discounts?.digital_member?.value > 0 && (
                          <p className="text-emerald-400">Digital: {feeCalcResult.rate_table_info?.membership_discounts?.digital_member?.value}%</p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Exportable Payment Request Card (for sharing with parents) */}
              {(feeCalcResult.comparison?.status === "underpaid" || feeCalcResult.comparison?.status === "not_compared") && (
                <div ref={feeExportRef} className="p-6 bg-slate-900 rounded-lg border border-slate-700">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-white">SCC Football Academy</h3>
                    <p className="text-slate-400 text-sm">Payment Request</p>
                  </div>
                  
                  {/* Player Info */}
                  <div className="mb-4 p-3 bg-slate-800/50 rounded">
                    <p className="text-sm text-slate-400">For:</p>
                    <p className="text-white font-medium">
                      {feeParams.child_name || "Academy Player"}
                      {feeParams.parent_name && <span className="text-slate-400"> (Parent: {feeParams.parent_name})</span>}
                    </p>
                  </div>

                  {/* Fee Breakdown Summary */}
                  <div className="mb-4 p-3 bg-slate-800/50 rounded space-y-2">
                    <p className="text-sm font-medium text-slate-300">Fee Calculation:</p>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-slate-400">{feeCalcResult.calculation?.parameters?.sessions_per_week} session(s)/week × {feeCalcResult.calculation?.parameters?.term_weeks} weeks</span>
                        <span className="text-white">${feeCalcResult.calculation?.breakdown?.subtotal_before_sibling?.toFixed(2) || feeCalcResult.calculation?.calculated_total?.toFixed(2)}</span>
                      </div>
                      {feeCalcResult.calculation?.breakdown?.sibling_discount > 0 && (
                        <div className="flex justify-between text-emerald-400">
                          <span>Sibling Discount</span>
                          <span>-${feeCalcResult.calculation?.breakdown?.sibling_discount?.toFixed(2)}</span>
                        </div>
                      )}
                      {feeCalcResult.calculation?.registration_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Registration Fee</span>
                          <span className="text-white">+${feeCalcResult.calculation?.registration_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold pt-2 border-t border-slate-600">
                        <span className="text-white">Total Fee</span>
                        <span className="text-purple-400">${feeCalcResult.calculation?.calculated_total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Status */}
                  {feeCalcResult.comparison?.actual_paid !== null && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-400">Amount Paid</span>
                        <span className="text-white">${feeCalcResult.comparison?.actual_paid?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm font-bold">
                        <span className="text-red-400">Outstanding Balance</span>
                        <span className="text-red-400">${Math.abs(feeCalcResult.comparison?.difference || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* QR Code */}
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-3 rounded-lg">
                      <QRCodeSVG 
                        value={`https://www.paynow.sg/pay?UEN=${feeCalcResult.qr_data?.uen}&amount=${
                          feeCalcResult.comparison?.status === "underpaid" 
                            ? Math.abs(feeCalcResult.comparison?.difference || 0).toFixed(2)
                            : feeCalcResult.qr_data?.amount?.toFixed(2)
                        }&ref=${feeCalcResult.qr_data?.reference}`}
                        size={100}
                        level="H"
                      />
                    </div>
                    <div className="flex-1 text-sm">
                      <p className="text-slate-400">Pay To:</p>
                      <p className="text-white font-medium">{feeCalcResult.qr_data?.company}</p>
                      <p className="text-slate-400 text-xs">UEN: {feeCalcResult.qr_data?.uen}</p>
                      <p className="text-emerald-400 font-bold text-lg mt-1">
                        ${feeCalcResult.comparison?.status === "underpaid" 
                          ? Math.abs(feeCalcResult.comparison?.difference || 0).toFixed(2)
                          : feeCalcResult.qr_data?.amount?.toFixed(2)}
                      </p>
                      <p className="text-yellow-400 text-xs">Ref: {feeCalcResult.qr_data?.reference}</p>
                    </div>
                  </div>

                  <p className="text-center text-slate-500 text-xs mt-4">
                    Scan QR code with PayNow to complete payment
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-6 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setShowFeeResult(false)} className="border-slate-600">
              Close
            </Button>
            {(feeCalcResult?.comparison?.status === "underpaid" || feeCalcResult?.comparison?.status === "not_compared") && (
              <>
                <Button 
                  onClick={downloadFeeQR}
                  variant="outline"
                  className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="download-fee-qr"
                >
                  <Download className="w-4 h-4 mr-2" /> QR Only
                </Button>
                <Button 
                  onClick={downloadFullCalculation}
                  className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  data-testid="download-full-calc"
                >
                  <Download className="w-4 h-4 mr-2" /> Export Payment Request
                </Button>
              </>
            )}
            <Button 
              onClick={() => {
                setShowFeeResult(false);
                setShowFeeCalculator(true);
              }}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              <Calculator className="w-4 h-4 mr-2" /> Recalculate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Detail drill-down */}
      <PaymentDetailDialog
        recordId={detailOpenId}
        open={!!detailOpenId}
        onClose={() => setDetailOpenId(null)}
      />
    </div>
  );
}
